/**
 * HTML template builder for the Lieferanten-Anfrage export.
 *
 * renderLieferantenAbfrage(data) → self-contained HTML string suitable
 * for an srcdoc iframe preview or window.print()-based PDF export.
 *
 * Mirrors kunden_konformitaet but anchored on supplier: lists the
 * components we sourced from the supplier with last-known SVHC info
 * and an answer column to be filled in by the supplier.
 */

import { generateRegulatoryNotes } from "../../../components/conformity/conformityPdf"

const CANDIDATE_LIST_VERSION = "31. Aktualisierung"
const CANDIDATE_LIST_DATE = "2024-06-27"
const CANDIDATE_LIST_ENTRIES = 240

function esc(s: string | null | undefined): string {
  return (s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type LieferantenAbfrageSubstanceRow = {
  componentId: string
  componentName: string
  casNumber: string
  substanceName: string
  lastDeclaredSvhcGehalt: string
}

export type LieferantenAbfrageComponent = {
  componentId: string
  componentName: string
  lastDeclaredSvhcPresent: string
  lastSupplierInfoDate: string
  rows: LieferantenAbfrageSubstanceRow[]
}

export type LieferantenAbfrageHtmlData = {
  issuer: string
  issuerAddress: string
  creationDate: string
  signatory: string
  signatoryRole: string
  signatoryPlace: string
  signatoryDate: string
  responseDueDate: string
  supplier: {
    supplierId: string
    supplierName: string
    contactName: string
    contactPosition: string
    contactLocation: string
    totalComponentCount: number
    componentsWithSvhcCount: number
    components: LieferantenAbfrageComponent[]
  }
}

// ---------------------------------------------------------------------------
// Components / substances table
// ---------------------------------------------------------------------------

function renderComponentTable(
  components: LieferantenAbfrageComponent[],
): string {
  if (components.length === 0) {
    return `
    <section>
      <h2>Bauteile</h2>
      <p class="declaration">
        Aktuell sind keine Bauteile zu diesem Lieferanten hinterlegt.
      </p>
    </section>`
  }

  let rowIndex = 0
  const rowsHtml = components
    .flatMap((c) => {
      if (c.rows.length === 0) {
        const tr = `
        <tr${rowIndex % 2 === 1 ? ' class="alt"' : ""}>
          <td>${esc(c.componentName)}</td>
          <td>${esc(c.lastDeclaredSvhcPresent) || "—"}</td>
          <td>—</td>
          <td>—</td>
          <td><div class="answer-box"></div></td>
        </tr>`
        rowIndex++
        return [tr]
      }
      return c.rows.map((r) => {
        const tr = `
        <tr${rowIndex % 2 === 1 ? ' class="alt"' : ""}>
          <td>${esc(c.componentName)}</td>
          <td>${esc(c.lastDeclaredSvhcPresent) || "—"}</td>
          <td>${esc(r.casNumber)}</td>
          <td>${esc(r.substanceName)}</td>
          <td><div class="answer-box"></div></td>
        </tr>`
        rowIndex++
        return tr
      })
    })
    .join("")

  return `
  <section>
    <h2>Bauteile und letzte Angabe</h2>
    <table>
      <thead>
        <tr>
          <th>Bauteil</th>
          <th>Letzte&nbsp;Angabe&nbsp;SVHC</th>
          <th>CAS-Nr.</th>
          <th>Stoffname</th>
          <th>Antwort Lieferant</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
  </section>`
}

// ---------------------------------------------------------------------------
// CSS — screen (iframe) + print
// ---------------------------------------------------------------------------

const CSS = `
  *, *::before, *::after { box-sizing: border-box; }

  body {
    margin: 0;
    padding: 16px 8px;
    background: #e4e4e7;
    font-family: Helvetica, Arial, sans-serif;
    font-size: 10pt;
    color: #1a1a1a;
  }

  .page {
    background: #fff;
    max-width: 760px;
    min-height: 520px;
    margin: 0 auto 16px;
    padding: 36px 44px 32px;
    box-shadow: 0 1px 6px rgba(0,0,0,.18);
    position: relative;
  }

  h1  { font-size: 17pt; margin: 0 0 3px; font-weight: bold; color: #111; }
  h2  { font-size: 10.5pt; font-weight: bold; color: #111; margin: 0 0 7px;
        padding-bottom: 4px; border-bottom: 1px solid #ececec; }
  p   { margin: 0; }
  hr  { border: none; border-top: 1px solid #e0e0e0; margin: 10px 0; }
  ul  { margin: 2px 0 0; padding-left: 16px; }
  li  { font-size: 9pt; line-height: 1.65; color: #333; }

  .subtitle    { font-size: 9pt; color: #777; }
  .declaration { font-size: 9pt; line-height: 1.6; color: #333; }

  .meta-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 24px;
    margin: 10px 0;
  }
  .meta-label {
    font-size: 7pt; color: #999; margin: 0 0 1px;
    text-transform: uppercase; letter-spacing: .04em;
  }
  .meta-value { font-size: 9.5pt; font-weight: 600; color: #222; margin: 0 0 8px; }

  section { margin-bottom: 14px; }

  table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 2px; }
  thead tr { background: #2d2d32; }
  th { padding: 5px 7px; text-align: left; font-weight: 600; color: #fff; font-size: 7.5pt; }
  td { padding: 4px 7px; color: #333; vertical-align: top; border-bottom: 1px solid #f0f0f0; }
  tr.alt td { background: #f5f5f8; }

  .answer-box { border: 1px dashed #888; min-height: 22px; min-width: 90px; }

  .signature { margin-top: 24px; }
  .sig-line { width: 70mm; border-top: 1px solid #555; margin-bottom: 5px; }
  .sig-name { font-size: 10pt; font-weight: bold; margin: 0 0 2px; }
  .sig-role { font-size: 8pt; color: #888; margin: 0 0 2px; }

  .page-footer {
    display: flex;
    justify-content: space-between;
    font-size: 7pt;
    color: #c0c0c0;
    border-top: 1px solid #eee;
    padding-top: 5px;
    margin-top: 20px;
  }

  @media print {
    body { background: #fff; padding: 0; }
    .page {
      box-shadow: none;
      margin: 0;
      max-width: 100%;
      min-height: auto;
      padding: 14mm 18mm 12mm;
      break-after: page;
    }
    .page:last-child { break-after: avoid; }
  }
`

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

export function renderLieferantenAbfrage(
  data: LieferantenAbfrageHtmlData,
): string {
  const s = data.supplier

  const intro =
    `im Rahmen unserer REACH-Konformitätspflichten gemäß Verordnung (EG) Nr. 1907/2006, Artikel 33, ` +
    `bitten wir Sie um eine aktualisierte Auskunft zu den nachfolgend aufgeführten Bauteilen, ` +
    `die wir von ${s.supplierName} beziehen.`

  const ask =
    `Bitte bestätigen oder aktualisieren Sie für jedes Bauteil, ob besonders besorgniserregende ` +
    `Stoffe (SVHC) gemäß der aktuellen ECHA-Kandidatenliste in einer Konzentration von mehr als ` +
    `0,1 Massenprozent enthalten sind. Wir bitten um Rückmeldung bis spätestens ${data.responseDueDate || "—"}.`

  const componentSection = renderComponentTable(s.components)

  const regulatoryItems = generateRegulatoryNotes()
    .split("\n")
    .filter((l) => l.trim())
    .map((l) => `<li>${esc(l)}</li>`)
    .join("")

  const body = `
  <div class="page">
    <header>
      <h1>Lieferantenanfrage SVHC</h1>
      <p class="subtitle">gem&#228;&#223; Verordnung (EG) Nr.&#160;1907/2006 (REACH), Artikel&#160;33</p>
      <hr>
    </header>

    <div class="meta-grid">
      <div>
        <p class="meta-label">Aussteller</p>
        <p class="meta-value">${esc(data.issuer)}</p>
        <p class="meta-label">Adresse</p>
        <p class="meta-value">${esc(data.issuerAddress)}</p>
        <p class="meta-label">Erstellungsdatum</p>
        <p class="meta-value">${esc(data.creationDate)}</p>
      </div>
      <div>
        <p class="meta-label">Empf&#228;nger</p>
        <p class="meta-value">${esc(s.supplierName)}</p>
        <p class="meta-label">Lieferanten-Nr.</p>
        <p class="meta-value">${esc(s.supplierId)}</p>
        <p class="meta-label">Ansprechpartner</p>
        <p class="meta-value">${esc(s.contactName)}${s.contactPosition ? ", " + esc(s.contactPosition) : ""}</p>
        <p class="meta-label">Kandidatenliste</p>
        <p class="meta-value">${esc(CANDIDATE_LIST_VERSION)} (${CANDIDATE_LIST_ENTRIES}&#160;Eintr&#228;ge,&#160;${esc(CANDIDATE_LIST_DATE)})</p>
      </div>
    </div>
    <hr>

    <section>
      <h2>Anliegen</h2>
      <p class="declaration">${esc(intro)}</p>
      <p class="declaration" style="margin-top:8px;">${esc(ask)}</p>
    </section>

    ${componentSection}

    <section>
      <h2>Allgemeine Best&#228;tigung</h2>
      <p class="declaration">
        Hiermit best&#228;tige ich, dass die obigen Angaben zum Stand vom <strong>${esc(data.creationDate)}</strong>
        dem aktuellen Kenntnisstand des Lieferanten entsprechen.
      </p>
    </section>

    <section>
      <h2>Regulatorische Hinweise</h2>
      <ul>${regulatoryItems}</ul>
    </section>

    <section class="signature">
      <div class="sig-line"></div>
      <p class="sig-name">${esc(s.contactName) || "—"}</p>
      <p class="sig-role">${esc(s.contactPosition)}</p>
      <p class="sig-role">${esc(s.contactLocation)}, ${esc(data.signatoryDate)}</p>
    </section>

    <div class="page-footer">
      <span>turnus.ai QMS &#183; Vertraulich</span>
      <span>REACH-Lieferantenanfrage &#183; ${esc(s.supplierName)} &#183; ${esc(data.creationDate)}</span>
    </div>
  </div>`

  return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Lieferantenanfrage SVHC &#8211; ${esc(s.supplierName)}</title>
<style>${CSS}</style>
</head>
<body>${body}</body>
</html>`
}
